import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { globalVars } from "../../global/GlobalVars.js";
import DataPicker from "./DataPicker.jsx";
import * as render from "../../utils/render.js";
import { de, enUS, it } from "date-fns/locale";
import * as language from "../../utils/language.js";
import * as XLSX from "xlsx";

//========================================================================
// Konfiguration der api-Schnittstelle
const counterDataUrl =
  window.location.port === "3000"
    ? `http://${window.location.hostname}:${globalVars.httpPort}/counter/`
    : `/api/counter/`;

//========================================================================
// Konstanten
const EMPTY_SELECTED_COUNTERS = [{ id: "" }, { id: "" }];

const localeMap = { de, en: enUS, it };

//========================================================================
// API-Funktionen
//========================================================================
const fetchData = async (id, startDate, endDate, resolution, setDataset) => {
  if (!id) {
    setDataset(null);
    return;
  }
  try {
    const response = await axios.get(counterDataUrl + "data", {
      params: {
        startDateString: startDate.toISOString(),
        endDateString: endDate.toISOString(),
        id,
        resolution,
      },
    });
    setDataset({
      data: response.data.data ?? [],
      info: {
        id: response.data.id,
        name: response.data.name || "",
        unit: response.data.unit || "",
        decimalPoints: response.data.decimalPoints || 0,
        type: response.data.type || "",
      },
    });
  } catch (error) {
    console.error("Fehler beim Abrufen der Daten:", error);
    setDataset(null);
  }
};

const fetchAllCounters = async (setAllCounters) => {
  try {
    const response = await axios.get(counterDataUrl + "allCounters");
    setAllCounters(response.data);
  } catch (error) {
    console.error("Fehler beim Abrufen der Zähler:", error);
  }
};

//========================================================================
// Formatierung
const formatValue = (value, info) => {
  if (value === undefined || value === null || value === "") return "";
  const dp = info?.decimalPoints ?? 0;
  const unit = info?.unit ?? "";
  return Number(value).toFixed(dp).replace(".", ",") + (unit ? ` ${unit}` : "");
};

// Gibt den Zeitschlüssel eines Eintrags zurück (je nach Auflösung)
const getTimeKey = (ts, resolution) => {
  const d = new Date(ts);
  if (resolution === "1_day") {
    // bei täglicher Auflösung: einfach "Tag" als einzige Spalte
    return "Gesamt";
  }
  // Stunden oder Minuten: HH:mm
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const getDayKey = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString("de-DE"); // dd.MM.yyyy
};

//========================================================================
// Pivot-Aufbereitung
const buildPivot = (dataArray, resolution) => {
  if (!dataArray?.length) return { days: [], timeSlots: [], matrix: {} };

  const matrix = {};
  const daySet = new Map(); // day -> sort key (timestamp des ersten Eintrags)
  const slotSet = new Map(); // slot -> sort key

  dataArray.forEach((entry) => {
    const day = getDayKey(entry.timestamp);
    const slot = getTimeKey(entry.timestamp, resolution);
    const ts = new Date(entry.timestamp).getTime();

    if (!daySet.has(day)) daySet.set(day, ts);
    if (!slotSet.has(slot)) slotSet.set(slot, ts);

    if (!matrix[day]) matrix[day] = {};
    matrix[day][slot] = entry.value;
  });

  const days = [...daySet.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([k]) => k);
  const timeSlots = [...slotSet.keys()].sort((a, b) => {
    if (a === "Gesamt") return 0;
    const [ah, am] = a.split(":").map(Number);
    const [bh, bm] = b.split(":").map(Number);
    return ah !== bh ? ah - bh : am - bm;
  });

  return { days, timeSlots, matrix };
};

//========================================================================
// Komponente Zählerauswertung
const Zählerauswertung = observer((props) => {
  const [dataset, setDataset] = useState(null);
  const [allCounters, setAllCounters] = useState([]);
  const [selectedCounters, setSelectedCounters] = useState(
    EMPTY_SELECTED_COUNTERS,
  );
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
  );
  const [endDate, setEndDate] = useState(new Date());
  const [resolution, setResolution] = useState("1_day");
  const [displayMode, setDisplayMode] = useState("value"); // "value" | "diff"

  const currentLocale = localeMap[globalVars.language] ?? de;
  const s = (val) => render.parseCssValue(val, globalVars.scaleFactor);

  useEffect(() => {
    const now = new Date();
    if (startDate > endDate) setStartDate(endDate);
    if (endDate > now) setEndDate(now);
  }, [startDate, endDate]);

  useEffect(() => {
    const id = selectedCounters[1]?.id;
    fetchData(id, startDate, endDate, resolution, setDataset);
  }, [selectedCounters, startDate, endDate, resolution]);

  useEffect(() => {
    fetchAllCounters(setAllCounters);
  }, []);

  //========================================================================
  // Pivot
  const {
    days,
    timeSlots,
    matrix: rawMatrix,
  } = useMemo(
    () => buildPivot(dataset?.data, resolution),
    [dataset, resolution],
  );

  // Bei Differenz-Modus: pro Zeile (Tag) die Differenz zum vorherigen Wert berechnen.
  const matrix = useMemo(() => {
    if (displayMode === "value") return rawMatrix;

    const diffMatrix = {};
    let prevValue = null;

    days.forEach((day) => {
      diffMatrix[day] = {};
      timeSlots.forEach((slot) => {
        const cur = rawMatrix[day]?.[slot];
        if (cur !== undefined && cur !== null) {
          diffMatrix[day][slot] = prevValue !== null ? cur - prevValue : null;
          prevValue = cur;
        }
      });
    });

    return diffMatrix;
  }, [rawMatrix, days, timeSlots, displayMode]);

  // Spaltenüberschrift je nach Modus
  const slotLabel = (slot) => {
    if (displayMode === "value" || slot === "Gesamt") return slot;
    const idx = timeSlots.indexOf(slot);
    if (idx === 0) return `– – ${slot}`;
    const prev = timeSlots[idx - 1];
    return `${prev} – ${slot}`;
  };

  //========================================================================
  // Download
  const downloadCSV = () => {
    try {
      const header = ["Datum", ...timeSlots.map(slotLabel)];
      const rows = days.map((day) => [
        day,
        ...timeSlots.map((slot) => {
          const v = matrix[day]?.[slot];
          return v !== undefined && v !== null
            ? formatValue(v, dataset?.info)
            : "";
        }),
      ]);

      const bom = "\uFEFF";
      const lines = [header, ...rows].map((r) => r.join("\t")).join("\n");
      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download =
        language.displayText(["Zähler", "contatore", "counter"]) + ".csv";

      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (err) {}
  };

  const downloadXLSX = () => {
    try {
      const header = ["Datum", ...timeSlots.map(slotLabel)];
      const rows = days.map((day) => [
        day,
        ...timeSlots.map((slot) => {
          const v = matrix[day]?.[slot];
          return v !== undefined && v !== null
            ? formatValue(v, dataset?.info)
            : "";
        }),
      ]);
      const wsData = [header, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        language.displayText(["Zähler", "contatore", "counter"]),
      );
      XLSX.writeFile(
        wb,
        language.displayText(["Zähler", "contatore", "counter"]) + ".xlsx",
      );
    } catch (err) {}
  };

  //========================================================================
  // Zellbreite je nach Auflösung
  //const cellW = resolution === "1_day" ? 200 : resolution === "1_hour" ? 90 : 70;
  const cellW =
    resolution === "1_day" ? 200 : resolution === "1_hour" ? 185 : 185;
  const dayColW = 110;
  const rowH = 32;
  const headerH = 36;

  //========================================================================
  // Render
  return (
    <div>
      {/* Hintergrund */}
      <div
        style={{
          position: "absolute",
          top: s("45px"),
          left: s("10px"),
          height: s("861px"),
          width: s("1900px"),
          backgroundColor: "#0d1018",
        }}
      />

      {/* ── Linke Seite: DataPicker + Pivot-Tabelle ── */}
      <div
        style={{
          position: "absolute",
          top: s("55px"),
          left: s("20px"),
          width: s("1450px"),
          height: s("840px"),
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* DataPicker */}
        <div style={{ flexShrink: 0, height: s("40px") }}>
          <DataPicker
            index={1}
            style={{
              top: "0px",
              left: "0px",
              height: s("40px"),
              width: s("1450px"),
              fontSize: s("14px"),
            }}
            allCounters={allCounters}
            selectedCounters={selectedCounters}
            setselectedCounters={setSelectedCounters}
          />
        </div>

        {/* Pivot-Tabelle scrollbar */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            marginTop: s("8px"),
            border: "1px solid #474a52",
            borderRadius: s("4px"),
          }}
        >
          {days.length === 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: s("60px"),
                color: "#606880",
                fontSize: s("13px"),
              }}
            >
              {language.displayText(["Keine Daten", "Nessun dato", "No data"])}
            </div>
          ) : (
            <table
              style={{
                borderCollapse: "collapse",
                tableLayout: "fixed",
                fontSize: s("13px"),
                color: "#a9b1bd",
                minWidth: "100%",
              }}
            >
              {/* Spaltenbreiten */}
              <colgroup>
                <col
                  style={{
                    width: s(`${dayColW}px`),
                    minWidth: s(`${dayColW}px`),
                  }}
                />
                {timeSlots.map((slot) => (
                  <col
                    key={slot}
                    style={{
                      width: s(`${cellW}px`),
                      minWidth: s(`${cellW}px`),
                    }}
                  />
                ))}
              </colgroup>

              {/* Header: Datum-Spalte + Zeitslots */}
              <thead>
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 3,
                      height: s(`${headerH}px`),
                      backgroundColor: "#2d323f",
                      borderBottom: "1px solid #474a52",
                      borderRight: "1px solid #474a52",
                      fontWeight: 600,
                      textAlign: "center",
                      padding: `0 ${s("8px")}`,
                      color: "#a9b1bd",
                    }}
                  >
                    {language.displayText(["Datum", "Data", "Date"])}
                  </th>
                  {timeSlots.map((slot) => (
                    <th
                      key={slot}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        height: s(`${headerH}px`),
                        backgroundColor: "#2d323f",
                        borderBottom: "1px solid #474a52",
                        borderRight: "1px solid #1e2330",
                        fontWeight: 500,
                        textAlign: "center",
                        padding: `0 ${s("4px")}`,
                        color: "#a9b1bd",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {slotLabel(slot)}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Datenzeilen: eine Zeile pro Tag */}
              <tbody>
                {days.map((day, di) => (
                  <tr key={day}>
                    {/* Datum-Zelle sticky links */}
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        height: s(`${rowH}px`),
                        backgroundColor: "#1a1f2b",
                        borderBottom: "1px solid #474a52",
                        borderRight: "1px solid #474a52",
                        textAlign: "center",
                        padding: `0 ${s("8px")}`,
                        fontWeight: 600,
                        color: "#6b7a99",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {day}
                    </td>
                    {/* Wert-Zellen */}
                    {timeSlots.map((slot, si) => {
                      const val = matrix[day]?.[slot];
                      const hasVal = val !== undefined && val !== null;
                      return (
                        <td
                          key={slot}
                          style={{
                            height: s(`${rowH}px`),
                            backgroundColor:
                              di % 2 === 0
                                ? si % 2 === 0
                                  ? "#0d1018"
                                  : "#0f1320"
                                : si % 2 === 0
                                  ? "#111520"
                                  : "#131825",
                            borderBottom: "1px solid #1e2330",
                            borderRight: "1px solid #1e2330",
                            textAlign: "center",
                            padding: `0 ${s("4px")}`,
                            color: hasVal ? "#cbd5e1" : "#3a3f50",
                          }}
                        >
                          {hasVal ? formatValue(val, dataset?.info) : "–"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Rechte Seite: Steuerung ── */}
      <div
        style={{
          position: "absolute",
          top: s("55px"),
          left: s("1500px"),
          width: s("400px"),
          display: "flex",
          flexDirection: "column",
          gap: s("12px"),
        }}
      >
        {/* Zeitraum */}
        <div
          style={{
            backgroundColor: "#2d323f",
            border: "1px solid #1e2330",
            borderRadius: s("6px"),
            padding: s("12px"),
            display: "flex",
            flexDirection: "column",
            gap: s("10px"),
          }}
        >
          <div
            style={{
              fontSize: s("13px"),
              fontWeight: 600,
              letterSpacing: "0.12em",
              color: "#a9b1bd",
              borderBottom: "1px solid #1e2a3a",
              paddingBottom: s("6px"),
              textAlign: "center",
            }}
          >
            {language.displayText(["Zeitraum", "Periodo", "Time Range"])}
          </div>

          {[
            {
              label: language.displayText(["Start", "Inizio", "Start"]),
              date: startDate,
              onChange: setStartDate,
            },
            {
              label: language.displayText(["Ende", "Fine", "End"]),
              date: endDate,
              onChange: setEndDate,
            },
          ].map(({ label, date, onChange }) => (
            <div
              key={label}
              style={{ display: "flex", alignItems: "center", gap: s("10px") }}
            >
              <span
                style={{
                  width: s("45px"),
                  fontSize: s("13px"),
                  color: "#a9b1bd",
                  flexShrink: 0,
                }}
              >
                {label}
              </span>
              <DatePicker
                selected={date}
                onChange={onChange}
                showTimeSelect
                timeIntervals={15}
                timeCaption={language.displayText(["Zeit", "tempo", "time"])}
                timeFormat="HH:mm"
                dateFormat="dd.MM.yyyy HH:mm"
                locale={currentLocale}
                customInput={
                  <input
                    style={{
                      width: s("220px"),
                      height: s("28px"),
                      background: "#0f1117",
                      border: "1px solid #2a3448",
                      borderRadius: s("6px"),
                      color: "#cbd5e1",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: s("13px"),
                      textAlign: "center",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  />
                }
              />
            </div>
          ))}
        </div>

        {/* Auflösung */}
        <div
          style={{
            backgroundColor: "#2d323f",
            border: "1px solid #1e2330",
            borderRadius: s("6px"),
            padding: s("12px"),
            display: "flex",
            flexDirection: "column",
            gap: s("8px"),
          }}
        >
          <div
            style={{
              fontSize: s("13px"),
              fontWeight: 600,
              letterSpacing: "0.12em",
              color: "#a9b1bd",
              borderBottom: "1px solid #1e2a3a",
              paddingBottom: s("6px"),
              textAlign: "center",
            }}
          >
            {language.displayText(["Auflösung", "Risoluzione", "Resolution"])}
          </div>
          <select
            style={{
              background: "#0f1117",
              border: "1px solid #2a3448",
              borderRadius: s("6px"),
              color: "#cbd5e1",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: s("13px"),
              padding: `0 ${s("10px")}`,
              height: s("30px"),
              width: "100%",
              outline: "none",
              cursor: "pointer",
            }}
            onChange={(e) => setResolution(e.target.value)}
            value={resolution}
          >
            <option value="1_day">1 Tag</option>
            <option value="1_hour">1 Stunde</option>
            <option value="1_minute">1 Minute</option>
          </select>
        </div>

        {/* Anzeigemodus */}
        <div
          style={{
            backgroundColor: "#2d323f",
            border: "1px solid #1e2330",
            borderRadius: s("6px"),
            padding: s("12px"),
            display: "flex",
            flexDirection: "column",
            gap: s("8px"),
          }}
        >
          <div
            style={{
              fontSize: s("13px"),
              fontWeight: 600,
              letterSpacing: "0.12em",
              color: "#a9b1bd",
              borderBottom: "1px solid #1e2a3a",
              paddingBottom: s("6px"),
              textAlign: "center",
            }}
          >
            {language.displayText(["Anzeigemodus", "Modalità", "Display mode"])}
          </div>
          <select
            style={{
              background: "#0f1117",
              border: "1px solid #2a3448",
              borderRadius: s("6px"),
              color: "#cbd5e1",
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: s("13px"),
              padding: `0 ${s("10px")}`,
              height: s("30px"),
              width: "100%",
              outline: "none",
              cursor: "pointer",
            }}
            onChange={(e) => setDisplayMode(e.target.value)}
            value={displayMode}
          >
            <option value="value">
              {language.displayText([
                "Zählerstand",
                "Lettura contatore",
                "Counter value",
              ])}
            </option>
            <option value="diff">
              {language.displayText([
                "Zählerdifferenz",
                "Differenza contatore",
                "Counter difference",
              ])}
            </option>
          </select>
        </div>

        {/* Download */}
        <div
          style={{
            backgroundColor: "#2d323f",
            border: "1px solid #1e2330",
            borderRadius: s("6px"),
            padding: s("12px"),
            display: "flex",
            flexDirection: "column",
            gap: s("8px"),
          }}
        >
          <div
            style={{
              fontSize: s("13px"),
              fontWeight: 600,
              letterSpacing: "0.12em",
              color: "#a9b1bd",
              borderBottom: "1px solid #1e2a3a",
              paddingBottom: s("6px"),
              textAlign: "center",
            }}
          >
            {language.displayText(["Download", "Download", "Download"])}
          </div>
          <div style={{ display: "flex", gap: s("8px") }}>
            <button
              style={{
                flex: 1,
                height: s("34px"),
                fontSize: s("13px"),
                backgroundColor: "#1e2330",
                border: "1px solid #2a3448",
                borderRadius: s("6px"),
                color: "#cbd5e1",
                cursor: "pointer",
              }}
              onClick={downloadCSV}
            >
              CSV
            </button>
            <button
              style={{
                flex: 1,
                height: s("34px"),
                fontSize: s("13px"),
                backgroundColor: "#1e2330",
                border: "1px solid #2a3448",
                borderRadius: s("6px"),
                color: "#cbd5e1",
                cursor: "pointer",
              }}
              onClick={downloadXLSX}
            >
              Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Zählerauswertung;
